import { Component, Fragment, h, Prop } from '@stencil/core';
import { Unsubscribe } from '@stencil/redux';
import { openCart } from '../../../../redux/actions';
import { RootState } from '../../../../redux/reducers';
import { store } from '../../../../redux/store';
import { Translations } from '../../../../redux/types';
import { AmountControlValues } from '../../../../types/types';
import { trans } from '../../../../utils/trans';

@Component({
  tag: 'rentware-amount-control-item-template',
  styleUrl: 'style-item.sass',
  shadow: false,
})
export class RentwareAmountControlItemTemplate {
  @Prop() controlValues: AmountControlValues;
  @Prop() onIncreaseValue: (number) => void;
  @Prop() onInputChange: (event) => void;
  @Prop() onValidateKey: (event) => void;

  @Prop({ mutable: true }) locale: string;
  @Prop({ mutable: true }) translations: Translations;

  unsubscribe!: Unsubscribe;
  openCart!: typeof openCart;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        locale: state.app.locale,
        translations: state.system.translations,
      };
    });

    store.mapDispatchToProps(this, {
      openCart,
    });
  }

  get containerClass() {
    let containerClass = 'AmountContainer ItemAmountContainer';

    if (!this.available) {
      containerClass += ' NotAvailableContainer';
    }

    return containerClass;
  }

  get minusButtonEnabled() {
    return this.controlValues.itemAmount !== 0 && (this.controlValues.itemAmount > this.controlValues.minAmount || this.minusButtonRemove);
  }

  get minusButtonRemove() {
    return this.controlValues.itemAmount === this.controlValues.minAmount || this.controlValues.itemAmount === 1;
  }

  get minusButtonStyle(): string {
    let minusButtonStyle = 'AmountButton';

    if (this.minusButtonEnabled) {
      minusButtonStyle += ' Waved';

      if (this.minusButtonRemove) {
        minusButtonStyle += ' Remove';
      }
    } else {
      minusButtonStyle += ' Disabled';
    }

    return minusButtonStyle;
  }

  get minusButtonCallback() {
    if (!this.minusButtonEnabled) {
      return () => {};
    }

    if (this.minusButtonRemove) {
      return this.controlValues.deleteArticleCallback;
    }

    return () => this.onIncreaseValue(-1);
  }

  get plusButtonStyleEnabled() {
    return this.controlValues.itemAmount < this.controlValues.maxAmount;
  }

  get plusButtonStyle(): string {
    let plusButtonStyle = 'AmountButton';

    if (this.plusButtonStyleEnabled) {
      plusButtonStyle += ' Waved';
    } else {
      plusButtonStyle += ' Disabled';
    }

    return plusButtonStyle;
  }

  get plusButtonCallback() {
    if (this.controlValues.disabled || !this.plusButtonStyleEnabled) {
      return null;
    }

    return () => this.onIncreaseValue(1);
  }

  get available() {
    return this.controlValues.maxAmount > 0 || this.controlValues.itemAmount > 0;
  }

  get singleAmount() {
    return this.controlValues.maxAmount === 0 || this.controlValues.maxAmount === 1 || this.controlValues.minAmount === this.controlValues.maxAmount;
  }

  get showInput() {
    if (this.singleAmount) {
      return false;
    }

    return true;
  }

  get notAvailableText() {
    return trans('articleBooking.notAvailable', this.locale, this.translations);
  }

  render() {
    return (
      <div class={this.containerClass}>
        {this.available && (
          <Fragment>
            <div tabindex="0" class={this.minusButtonStyle} onClick={this.minusButtonCallback}>
              <svg width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1H5H9" stroke-linecap="round" />
              </svg>
            </div>

            {this.showInput && (
              <input class="AmountInput" maxlength="3" type="number" value={String(this.controlValues.itemAmount)} onKeyPress={this.onValidateKey} onChange={this.onInputChange} />
            )}

            <div tabindex="0" class={this.plusButtonStyle} onClick={this.plusButtonCallback}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 4.94092H5M9 4.94092H5M5 4.94092V1M5 4.94092V9" stroke-linecap="round" />
              </svg>
            </div>

            <div class="Divider" />

            <div class="AmountButton Waved" onClick={this.openCart}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.6428 16.4286C16.6428 16.9414 16.2271 17.3571 15.7142 17.3571C15.2014 17.3571 14.7856 16.9414 14.7856 16.4286C14.7856 15.9157 15.2014 15.5 15.7142 15.5C16.2271 15.5 16.6428 15.9157 16.6428 16.4286Z" />
                <path d="M8.78561 16.4286C8.78561 16.9414 8.36987 17.3571 7.85704 17.3571C7.3442 17.3571 6.92847 16.9414 6.92847 16.4286C6.92847 15.9157 7.3442 15.5 7.85704 15.5C8.36987 15.5 8.78561 15.9157 8.78561 16.4286Z" />
                <path
                  d="M6.78564 12.1428H14.3495C15.6 12.1428 16.7194 11.3671 17.1585 10.1962L18.0647 7.77968C18.3098 7.12593 17.8266 6.42856 17.1284 6.42856H7.14279"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path d="M1.78564 2.5L4.64279 3.92857L7.49993 15H15.7142" />
              </svg>
            </div>
          </Fragment>
        )}

        {!this.available && (
          <div class="TextButtonContainer TextButtonNotAvailable">
            <svg width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M9.26795 2C10.0377 0.666667 11.9623 0.666667 12.7321 2L20.5263 15.5C21.2961 16.8333 20.3338 18.5 18.7942 18.5H3.20577C1.66617 18.5 0.70392 16.8333 1.47372 15.5L9.26795 2Z"
                stroke-width="2"
              />
            </svg>

            <span>{this.notAvailableText}</span>
          </div>
        )}

        {this.controlValues.disabled && <div class="AmountWaiting" />}
      </div>
    );
  }
}
